import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faTimes,
  faBars,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { supabase } from '../lib/supabaseClient'; // Import supabase client
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const OperasyonYetkilileri = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authorities, setAuthorities] = useState([]);
  const [filteredAuthorities, setFilteredAuthorities] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentAuthority, setCurrentAuthority] = useState({
    id: null,
    firstName: '',
    lastName: '',
    phoneNumber: '',
    position: '',
    department: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const [currentPage, setCurrentPage] = useState(1);
  const [authoritiesPerPage, setAuthoritiesPerPage] = useState(5); // Number of authorities per page

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Fetch data from Supabase
  const fetchAuthorities = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('operation_auths')
        .select('*');

      if (error) {
        throw error;
      }

      setAuthorities(data);
    } catch (error) {
      console.error('Error fetching authorities:', error);
      enqueueSnackbar('Yetkililer yüklenirken bir hata oluştu', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchAuthorities();
  }, [fetchAuthorities]);

  // Set filtered authorities whenever authorities change
  useEffect(() => {
    setFilteredAuthorities(authorities);
  }, [authorities]);

  // Reset to first page when authoritiesPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [authoritiesPerPage]);

  // Pagination logic
  const indexOfLastAuthority = currentPage * authoritiesPerPage;
  const indexOfFirstAuthority = indexOfLastAuthority - authoritiesPerPage;
  const currentAuthorities = filteredAuthorities.slice(
    indexOfFirstAuthority,
    indexOfLastAuthority
  );
  const totalPages = Math.ceil(filteredAuthorities.length / authoritiesPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleAuthoritiesPerPageChange = (e) => {
    setAuthoritiesPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleOpenDialog = (authority = null) => {
    if (authority) {
      setCurrentAuthority({
        id: authority.id,
        firstName: authority.name,
        lastName: authority.surname,
        phoneNumber: authority.phone,
        position: '',
        department: '',
      });
      setIsEditing(true);
    } else {
      setCurrentAuthority({
        id: null,
        firstName: '',
        lastName: '',
        phoneNumber: '',
        position: '',
        department: '',
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentAuthority((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      if (isEditing) {
        // Update existing authority
        const { error } = await supabase
          .from('operation_auths')
          .update({
            name: currentAuthority.firstName,
            surname: currentAuthority.lastName,
            phone: currentAuthority.phoneNumber,
          })
          .eq('id', currentAuthority.id);

        if (error) throw error;

        enqueueSnackbar('Yetkili başarıyla güncellendi', {
          variant: 'success',
        });
      } else {
        // Add new authority
        const { error } = await supabase.from('operation_auths').insert([
          {
            name: currentAuthority.firstName,
            surname: currentAuthority.lastName,
            phone: currentAuthority.phoneNumber,
          },
        ]);

        if (error) throw error;

        enqueueSnackbar('Yetkili başarıyla eklendi', { variant: 'success' });
      }

      // Refresh the data
      fetchAuthorities();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving authority:', error);
      enqueueSnackbar('Bir hata oluştu', { variant: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu yetkiliyi silmek istediğinizden emin misiniz?')) {
      try {
        const { error } = await supabase
          .from('operation_auths')
          .delete()
          .eq('id', id);

        if (error) throw error;

        // Update local state
        setAuthorities((prev) => prev.filter((item) => item.id !== id));
        enqueueSnackbar('Yetkili silindi', { variant: 'info' });
      } catch (error) {
        console.error('Error deleting authority:', error);
        enqueueSnackbar('Silme işlemi sırasında bir hata oluştu', {
          variant: 'error',
        });
      }
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between pb-6">
            <div>
              <h1 className="text-2xl font-medium text-gray-800">
                Operasyon Yetkilileri
              </h1>
              <p className="text-sm text-gray-500">
                Toplam Yetkili: {authorities.length}
              </p>
            </div>
            <button
              onClick={() => handleOpenDialog()}
              className="bg-[#4263EB] text-white px-4 py-2 rounded-md hover:bg-[#3b5bd9] flex items-center"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Yeni Yetkili Ekle
            </button>
          </div>

          {/* Authorities List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Ad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Soyad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Telefon Numarası
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                      </div>
                    </td>
                  </tr>
                ) : authorities.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      Kayıtlı yetkili bulunamadı
                    </td>
                  </tr>
                ) : (
                  currentAuthorities.map((authority) => (
                    <tr key={authority.id}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {authority.name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {authority.surname}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {authority.phone}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => handleOpenDialog(authority)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          <FontAwesomeIcon icon={faEdit} className="mr-1" />
                          Düzenle
                        </button>
                        <button
                          onClick={() => handleDelete(authority.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FontAwesomeIcon icon={faTrash} className="mr-1" />
                          Sil
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {!loading && authorities.length > 0 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex flex-col space-y-3 sm:hidden">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Önceki
                    </button>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-700 mr-2">
                        Sayfa başına:
                      </span>
                      <select
                        value={authoritiesPerPage}
                        onChange={handleAuthoritiesPerPageChange}
                        className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                    </div>
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Sonraki
                    </button>
                  </div>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div className="flex items-center">
                    <p className="text-sm text-gray-700 mr-4">
                      Görüntülenen{' '}
                      <span className="font-medium">
                        {indexOfFirstAuthority + 1}
                      </span>{' '}
                      -{' '}
                      <span className="font-medium">
                        {Math.min(
                          indexOfLastAuthority,
                          filteredAuthorities.length
                        )}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium">
                        {filteredAuthorities.length}
                      </span>{' '}
                      sonuç
                    </p>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-700 mr-2">
                        Sayfa başına:
                      </span>
                      <select
                        value={authoritiesPerPage}
                        onChange={handleAuthoritiesPerPageChange}
                        className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <nav
                      className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                      aria-label="Pagination"
                    >
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                          currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          className="h-4 w-4"
                        />
                      </button>

                      {/* Page Numbers */}
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        // Show current page, first page, last page, and one page before and after current
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= currentPage - 1 &&
                            pageNumber <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNumber}
                              onClick={() => paginate(pageNumber)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === pageNumber
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        }
                        // Show dots if there's a gap
                        if (
                          pageNumber === currentPage - 2 ||
                          pageNumber === currentPage + 2
                        ) {
                          return (
                            <span
                              key={pageNumber}
                              className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-gray-700"
                            >
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}

                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                          currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="h-4 w-4"
                        />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Modal */}
          {openDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {isEditing ? 'Yetkili Düzenle' : 'Yeni Yetkili Ekle'}
                  </h3>
                  <button
                    onClick={handleCloseDialog}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ad
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={currentAuthority.firstName || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Soyad
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={currentAuthority.lastName || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon Numarası
                    </label>
                    <input
                      type="text"
                      name="phoneNumber"
                      value={currentAuthority.phoneNumber || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="px-4 py-3 border-t flex justify-end space-x-3">
                  <button
                    onClick={handleCloseDialog}
                    className="px-4 py-2 border text-gray-700 rounded-md hover:bg-gray-100"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Kaydet
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default OperasyonYetkilileri;
